import React, { useEffect, useState, useRef } from 'react';
import FilterDropdown from './FilterDropdown';
import ActionButtons from './ActionButtons';
import { useQueryBuilder } from '../context/QueryBuilderContext';
import { FILTER_OPERATORS } from '../../../utils/constants';

interface LastModifiedByFilterDropdownProps {
  linkedSpaces: string[];
  closeDropdown: () => void;
  fetchSpaceUsers: (payload: any, headers: any, options: any) => Promise<any>;
}

const LIMIT = 10;
const DEBOUNCE_DELAY = 300;

const LastModifiedByFilterDropdown: React.FC<LastModifiedByFilterDropdownProps> = ({ linkedSpaces, closeDropdown, fetchSpaceUsers }) => {
  const { queryBuilder, setCondition, getConditionValue, removeCondition } = useQueryBuilder();
  const [spacesUsers, setSpacesUsers] = useState<any[]>([]);
  const [filteredUsers, setFilteredUsers] = useState<any[]>([]);
  const [selectedItems, setSelectedItems] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  const [skip, setSkip] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const [searchText, setSearchText] = useState('');
  const [debouncedSearchText, setDebouncedSearchText] = useState('');

  const dropdownRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const timeout = setTimeout(() => {
      setSkip(0);
      setHasMore(true);
      setDebouncedSearchText(searchText);
    }, DEBOUNCE_DELAY);

    return () => clearTimeout(timeout);
  }, [searchText]);

  const getSpacesUsers = async () => {
    if (loading || !hasMore) return;

    setLoading(true);
    try {
      const payload = {
        space_uids: linkedSpaces
      };
      const res = await fetchSpaceUsers(payload, {}, {});
      const seen = new Set<string>();
      const result = Object.entries(res).flatMap(([_, users]: any) =>
        users.map((user) => ({
          uid: user.userUid,
          name: `${user.firstName} ${user.lastName}`
        }))
      ).filter((user) => {
        if (seen.has(user.uid)) return false;
        seen.add(user.uid);
        return true;
      });
      setSpacesUsers(result);
      setFilteredUsers(result.slice(0, LIMIT));
      setHasMore(result.length > LIMIT);
    } catch (err) {
      console.error('Failed to fetch spacesUsers:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    getSpacesUsers();
  }, []);

  const searchUsers = () => {
    try {
      const filtered = debouncedSearchText.length
        ? spacesUsers.filter((user) => user.name.toLowerCase().includes(debouncedSearchText.toLowerCase()))
        : spacesUsers;
      if (skip === 0) {
        setFilteredUsers(filtered.slice(0, LIMIT));
      } else {
        setFilteredUsers((prev) => [...prev, ...filtered.slice(skip, skip + LIMIT)]);
      }

      setHasMore(filtered.length > skip + LIMIT);
    } catch (error) {
      console.error('Error searching users:', error);
    }
  };

  useEffect(() => {
    searchUsers();
  }, [debouncedSearchText, skip]);

  const handleScroll = () => {
    if (!dropdownRef.current || loading || !hasMore) return;

    const { scrollTop, scrollHeight, clientHeight } = dropdownRef.current;
    if (scrollTop + clientHeight >= scrollHeight - 20) {
      setSkip((prev) => prev + LIMIT);
    }
  };

  const clearAll = () => {
    setSelectedItems([]);
  };

  const applyFilter = () => {
    if (selectedItems.length > 0) {
      setCondition('updated_by', FILTER_OPERATORS.IN, selectedItems);
    } else {
      removeCondition('updated_by');
    }

    closeDropdown();
  };

  useEffect(() => {
    const existing = getConditionValue('updated_by');
    if (existing?.[FILTER_OPERATORS.IN]) {
      setSelectedItems(existing[FILTER_OPERATORS.IN]);
    }
  }, [queryBuilder]);

  return (
    <div>
      <input
        type="text"
        value={searchText}
        onChange={(e) => setSearchText(e.target.value)}
        placeholder="Start typing to see results..."
        style={{ width: '100%', padding: '8px', marginBottom: '8px', boxSizing: 'border-box' }}
      />

      <div ref={dropdownRef} onScroll={handleScroll} style={{ maxHeight: '300px', overflowY: 'auto' }}>
        <FilterDropdown
          data={filteredUsers.map((user) => ({
            name: user.name,
            id: user.uid
          }))}
          field="name"
          selectedItems={selectedItems}
          setSelectedItems={setSelectedItems}
          loading={loading}
        />
      </div>

      <ActionButtons closeDropdown={closeDropdown} clearAll={clearAll} applyFilter={applyFilter} />
    </div>
  );
};

export default LastModifiedByFilterDropdown;
